<template>
	<header :class="props.hero.backgroundColor">
		<div class="container">
			<div
				:class="[
					'display-flex flex-wrap-wrap align-items-center py-350 py-500-lg position-relative text-align-center-smo',
					{ 'gutters-300-md': !thirdPartyAuthStore.isSignupEnabled },
					textColor
				]"
			>
				<div class="col-6-md">
					<img
						v-if="props.logo"
						:src="props.logo"
						alt="Logo"
						class="mb-100 mb-300-lg display-inline-block img-native-width"
						data-test="asset-hero-logo"
					/>
					<p
						v-else-if="props.hero.heading"
						class="label-sm text-transform-uppercase mb-100 mb-225-lg"
						:class="labelTextColor"
						data-test="asset-hero-eyebrow"
						v-html="props.hero.heading"
					></p>
					<h1 v-if="props.hero.title" :class="headingSize" v-html="props.hero.title"></h1>
					<p v-if="props.hero.description" class="body-lg" v-html="props.hero.description"></p>
					<div
						:class="[
							'display-flex flex-direction-column mt-100 mt-225-lg',
							{ 'flex-direction-column-reverse gap-100': thirdPartyAuthStore.isSignupEnabled }
						]"
					>
						<slot name="form">
							<MarketingHeroCta
								v-if="formConfig"
								:component-id="componentId"
								:form-config="formConfig"
								:offering-id="offeringId"
								:is-dark-mode="!textColorDark"
								data-test="hero-lead-capture"
							/>
						</slot>
						<slot> </slot>
					</div>
				</div>
				<slot name="hero-image">
					<div
						v-if="props.hero.imageUrl"
						:class="['col-6-md mt-225-smo', { 'pl-300-md': thirdPartyAuthStore.isSignupEnabled }]"
					>
						<img v-reveal:fade-up :src="props.hero.imageUrl" :alt="`${props.hero.heading} hero graphic`" />
					</div>
				</slot>
			</div>
		</div>
	</header>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import MarketingHeroCta from '@components/marketing-site/marketing-hero-cta.vue';
import { MarketingImageHero } from 'types/layout';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';

interface Props {
	componentId?: string;
	hero: MarketingImageHero;
	formConfig?: LeadCaptureFormConfig;
	offeringId?: string;
	logo?: string;
}

const props = withDefaults(defineProps<Props>(), {
	componentId: undefined,
	formConfig: undefined,
	offeringId: undefined,
	logo: undefined
});

const thirdPartyAuthStore = useThirdPartyAuthStore();

const textColorDark = computed((): boolean => {
	switch (props.hero.backgroundColor) {
		case 'bg-color-content-primary':
		case 'bg-color-data-viz-dark-slate':
		case 'gray-80':
		case 'bg-color-rust':
		case 'bg-color-data-primary-dark':
			return false;
		case 'bg-color-gold-light':
		case 'bg-color-white':
		case 'bg-color-greige':
		case 'bg-color-bg-tertiary':
		case 'bg-color-data-secondary':
		default:
			return true;
	}
});

const headingSize = computed((): string => props.hero.headingSize ?? 'heading-1');

const labelTextColor = computed((): string => {
	return textColorDark.value ? 'text-color-content-secondary' : 'text-color-gray-20';
});

const textColor = computed((): string => {
	return textColorDark.value ? 'text-color-content-primary' : 'text-color-white';
});
</script>
